import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { fetchTherapeuticArea, generateNovaReport, downloadNovaReport } from '../services/analyticsService';
import MultiSelectDropdown from '../components/shared/MultiSelectDropdown';
import { NovaQuickAnalysisBar } from '../components/shared/NovaQuickAnalysis';
import ReportLanguageToggle, { useReportLanguage } from '../components/shared/ReportLanguageToggle';
import './Suppliers.css';

const CHART_CARD = { background: 'white', borderRadius: 14, border: '1px solid #e3e8ef', padding: '20px 16px', display: 'flex', flexDirection: 'column' };
const COLORS = ['#0D5C4F','#0B3D6B','#3B1F6E','#8B4513','#0F6B5B','#1466A7','#5C318C','#AB611F','#18A78F','#1E8FE0','#7D43AA','#E49937'];

function NovaInsight({ text }) {
  if (!text) return null;
  return (
    <div style={{ ...CHART_CARD, padding: '14px 18px', marginBottom: 20, flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
      <span className="ai-badge" style={{ flexShrink: 0, marginTop: 1 }}>✦ Nova</span>
      <div style={{ fontSize: 13, color: 'var(--navy)', lineHeight: 1.55 }}>{text}</div>
    </div>
  );
}

export default function TherapeuticAreaDevelopments() {
  const { instance: msalInstance } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [selectedAreas, setSelectedAreas] = useState(() => {
    const p = new URLSearchParams(location.search);
    const a = p.get('area');
    return a ? new Set([a]) : new Set();
  });
  const [exportingPptx, setExportingPptx] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [exportErr, setExportErr] = useState('');
  const { lang: reportLang, setLang: setReportLang } = useReportLanguage();

  const load = useCallback(() => {
    setLoading(true); setErr('');
    const params = {};
    if (areaFilter.trim()) params.area = areaFilter.trim();
    fetchTherapeuticArea(msalInstance, params)
      .then(d => { setData(d); if (d.areas && selectedAreas.size === 0) setSelectedAreas(new Set(d.areas.slice(0, 6))); })
      .catch(() => setErr('Failed to load therapeutic area data'))
      .finally(() => setLoading(false));
  }, [areaFilter, msalInstance]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const chartData = useMemo(() => {
    if (!data?.series || !data?.quarters) return [];
    return data.quarters.map(q => {
      const pt = { quarter: q };
      for (const area of selectedAreas) {
        const s = data.series[area];
        const found = s?.find(p => p.quarter === q);
        pt[area] = found ? found.cumulative : 0;
      }
      return pt;
    });
  }, [data, selectedAreas]);

  const handleExport = useCallback(async (format) => {
    const setLoading = format === 'pptx' ? setExportingPptx : setExportingXlsx;
    setLoading(true); setExportErr('');
    try {
      const areas = selectedAreas.size > 0 ? Array.from(selectedAreas) : [];
      const filters = areas.length > 0 ? { areas } : undefined;
      const meta = await generateNovaReport(msalInstance, { reportType: 'therapeutic-area', format, filters, language: reportLang });
      const blob = await downloadNovaReport(msalInstance, meta.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = meta.filename || `therapeutic-area-report.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { setExportErr('Export failed. Please try again.'); }
    finally { setLoading(false); }
  }, [msalInstance, selectedAreas, reportLang]);

  // Sync URL ↔ selectedAreas
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (selectedAreas.size === 1) {
      const [only] = selectedAreas;
      if (p.get('area') !== only) navigate(`?area=${encodeURIComponent(only)}`, { replace: true });
    } else if (p.has('area')) {
      navigate(location.pathname, { replace: true });
    }
  }, [selectedAreas, location.search, location.pathname, navigate]);

  const scopeToArea = useCallback((area) => {
    setSelectedAreas(new Set([area]));
  }, []);

  const toggleArea = (a) => {
    setSelectedAreas(prev => {
      const next = new Set(prev);
      if (next.has(a)) next.delete(a); else next.add(a);
      return next;
    });
  };

  const btnBase = {
    padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #156082',
  };

  return (
    <div className="sup-page">
      <div className="mb-3" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: 20, color: 'var(--navy)', margin: 0 }}>Therapeutic area developments</h2>
          <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>FocusOne &middot; Supplier Analytics</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {exportErr && <span style={{ fontSize: 11, color: 'var(--red)' }}>{exportErr}</span>}
          <ReportLanguageToggle value={reportLang} onChange={setReportLang} compact />
          <button type="button" className="sup-badge teal-soft" disabled={exportingPptx} onClick={() => handleExport('pptx')} style={{
            ...btnBase, cursor: exportingPptx ? 'wait' : 'pointer', background: exportingPptx ? '#e8e8e8' : undefined,
          }}>
            {exportingPptx ? 'Generating...' : 'Download PowerPoint'}
          </button>
          <button type="button" className="sup-badge teal-soft" disabled={exportingXlsx} onClick={() => handleExport('xlsx')} style={{
            ...btnBase, cursor: exportingXlsx ? 'wait' : 'pointer', background: exportingXlsx ? '#e8e8e8' : undefined,
          }}>
            {exportingXlsx ? 'Generating...' : 'Download Excel'}
          </button>
        </div>
      </div>

      {data?.summary && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
          <div className="sup-kpi" style={{ flex: '1 1 140px', maxWidth: 200 }}>
            <div className="sup-kpi-lbl">Areas</div><div className="sup-kpi-val">{data.summary.totalAreas}</div>
          </div>
          <div className="sup-kpi" style={{ flex: '1 1 140px', maxWidth: 200 }}>
            <div className="sup-kpi-lbl">Quarters</div><div className="sup-kpi-val">{data.summary.totalQuarters}</div>
          </div>
        </div>
      )}

      <NovaQuickAnalysisBar
        placeholder="Quick analysis — type a therapeutic area..."
        knownEntities={data?.areas || []}
        entityLabel="therapeutic area"
        reportType="therapeutic-area"
        filterBuilder={(entity) => ({ areas: [entity] })}
        onSubmit={(entity) => { setSelectedAreas(new Set([entity])); setAreaFilter(''); }}
      />

      <NovaInsight text={data?.novaSummary} />

      {/* Multi-select dropdown filter */}
      {data?.areas && data.areas.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 4, fontSize: 11, color: 'var(--t2)', fontWeight: 500 }}>
            Filter therapeutic areas {selectedAreas.size > 0 && <span style={{ color: 'var(--t3)' }}>({selectedAreas.size} selected)</span>}
          </div>
          <MultiSelectDropdown
            options={data.areas}
            selected={selectedAreas}
            onToggle={toggleArea}
            onClear={() => setSelectedAreas(new Set())}
            onSelectAll={() => setSelectedAreas(new Set(data.areas))}
            placeholder="Filter areas..."
            searchPlaceholder="Search areas..."
          />
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner-border spinner-border-sm me-2" role="status" />Loading...</div>}
      {err && <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e3e8ef', padding: '40px 24px', textAlign: 'center' }}><p style={{ margin: 0, fontSize: 14, color: 'var(--red)' }}>{err}</p></div>}

      {!loading && !err && data && (
        <>
          {/* Trend chart */}
          {chartData.length > 0 && selectedAreas.size > 0 && (
            <div style={{ ...CHART_CARD, marginBottom: 20 }}>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 15, color: 'var(--navy)', marginBottom: 12 }}>Cumulative Products by Therapeutic Area</div>
              <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                    <XAxis dataKey="quarter" tick={{ fontSize: 10, fill: '#6c757d' }} interval={Math.max(0, Math.floor(chartData.length / 12))} />
                    <YAxis tick={{ fontSize: 11, fill: '#6c757d' }} width={40} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {Array.from(selectedAreas).map((a, i) => (
                      <Line key={a} type="monotone" dataKey={a} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Leaderboard */}
          <div className="sup-table-wrap">
            <div style={{ padding: '10px 14px', fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              Fastest-Growing Areas (Recent 2 Quarters)
            </div>
            <div className="sup-table-scroll">
              <table className="sup-table">
                <thead><tr><th>Area</th><th>Recent Growth</th><th>Prior Growth</th><th>Cumulative</th></tr></thead>
                <tbody>
                  {(data.leaderboard || []).map((r, i) => (
                    <tr key={i} role="button" tabIndex={0} style={{ cursor: 'pointer' }}
                      onClick={() => scopeToArea(r.dimension)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); scopeToArea(r.dimension); } }}
                    >
                      <td style={{ fontWeight: 600 }}>{r.dimension}</td>
                      <td><span className="sup-badge teal-soft">+{r.recentGrowth}</span></td>
                      <td>{r.priorGrowth}</td>
                      <td><strong>{r.cumulative}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 8, fontStyle: 'italic' }}>Based on recorded product history.</div>
        </>
      )}
    </div>
  );
}
